import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Modal } from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import EditableModal from "../../components/layout/editableModal/EditableModal";
import SearchSelectModal from "../../components/layout/searchSelectModal/SearchSelectModal";
import templateIcon from "../../media/template_plan.png";
import GetData from "../../services/getData";
import "./treatmentPlanTemplate.css";
import "../patient/tabs/PatientPlan.css";

const editServiceFields = (t) => [
    { name: "name", label: t("serviceFields.name") },
    { name: "amount_base", label: t("serviceFields.amountBase"), type: "number" },
    { name: "composition", label: t("serviceFields.composition"), type: "textarea" },
    { name: "technology", label: t("serviceFields.technology"), type: "textarea" },
];

const COURSE_COLOR_OPTIONS = [
    { labelKey: "colors.paleYellow", value: "#fffae0" },
    { labelKey: "colors.paleGreen", value: "#eaf9ea" },
    { labelKey: "colors.paleBlue", value: "#d4ffff" },
    { labelKey: "colors.palePink", value: "#ffdae0" },
    { labelKey: "colors.paleOrange", value: "#ffeacb" },
    { labelKey: "colors.paleLavender", value: "#e6e6fa" },
    { labelKey: "colors.paleMint", value: "#056a0545" },
    { labelKey: "colors.palePeach", value: "#ffdab996" },
    { labelKey: "colors.paleSky", value: "#aadbf999" },
    { labelKey: "colors.paleLilac", value: "#c793f77a" },
];

const normalizeCourseTitle = (value) => {
    if (value == null) return "";
    const s = String(value).trim();
    if (!s) return "";
    // иногда "пустое" название прилетает как 0 / "0" / "0.0"
    const n = Number(s.replace(",", "."));
    if (!Number.isNaN(n) && n === 0) return "";
    if (s === "0") return "";
    return s;
};

const toCourseId = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    if (!s || s === "0") return null;
    return s;
};

const createCourseId = () =>
    `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const GROUP_TYPE_OPTIONS = [
    { value: "procedure", labelKey: "groupTypes.procedure" },
    { value: "diagnostic", labelKey: "groupTypes.diagnostic" },
    { value: "therapy", labelKey: "groupTypes.therapy" },
    { value: "consultation", labelKey: "groupTypes.consultation" },
    { value: "peptides", labelKey: "groupTypes.peptides" },
    { value: "manipulations", labelKey: "groupTypes.manipulations" },
    { value: "procedure_extra", labelKey: "groupTypes.procedure_extra" },
];

const GROUP_TYPE_LABEL_KEYS = Object.fromEntries(
    GROUP_TYPE_OPTIONS.map(({ value, labelKey }) => [value, labelKey]),
);

const WITHOUT_APPOINTMENTS_GROUP_TYPES = new Set([
    "therapy",
    "diagnostic",
    "manipulations",
]);

const isWithoutAppointmentsGroupType = (type) =>
    WITHOUT_APPOINTMENTS_GROUP_TYPES.has(type || "");

const WEEK_DAYS_FALLBACK = [];

const getGroupAppointmentLimit = (group, defaultCount) => {
    const groupLimit = Number(group?.count_appointment);
    if (Number.isFinite(groupLimit) && groupLimit > 0) {
        return groupLimit;
    }
    return Number.isFinite(defaultCount) && defaultCount > 0 ? defaultCount : 1;
};

const getGroupColumnType = (group) => {
    const groupType = Number(group?.column_type);
    if (Number.isFinite(groupType) && groupType >= 1 && groupType <= 4) {
        return groupType;
    }
    return 1;
};

const getAppointmentHeaderLabel = (appointmentNumber, appointmentHeaderType, t, weekDaysShort) => {
    const index = Math.max(appointmentNumber - 1, 0);
    const days = Array.isArray(weekDaysShort) && weekDaysShort.length
        ? weekDaysShort
        : WEEK_DAYS_FALLBACK;

    if (!days.length) {
        return t("appointment", { number: appointmentNumber });
    }

    if (appointmentHeaderType === 2) {
        return days[index % days.length];
    }
    if (appointmentHeaderType === 3) {
        const even = days.filter((_, i) => (i + 1) % 2 === 0);
        return even.length ? even[index % even.length] : days[index % days.length];
    }
    if (appointmentHeaderType === 4) {
        const odd = days.filter((_, i) => (i + 1) % 2 !== 0);
        return odd.length ? odd[index % odd.length] : days[index % days.length];
    }

    return t("appointment", { number: appointmentNumber });
};

const createEmptyStructure = () => ({
    version: 1,
    count_appointment: 4,
    groups: [],
    courses: [],
});

const normalizeStructure = (raw) => {
    if (!raw || typeof raw !== "object") {
        return createEmptyStructure();
    }

    if (raw.version === 1) {
        return {
            version: 1,
            count_appointment: Number(raw.count_appointment) || 1,
            groups: Array.isArray(raw.groups)
                ? raw.groups.map((g, gi) => ({
                    id: g.id || `g_${gi + 1}`,
                    name: g.name || "",
                    type: g.type || "procedure",
                    count_appointment: Number(g.count_appointment) || 0,
                    column_type: getGroupColumnType(g),
                    items: Array.isArray(g.items)
                        ? g.items.map((item, ii) => ({
                            id: item.id || `i_${gi + 1}_${ii + 1}`,
                            name: item.name || "",
                            composition: item.composition || "",
                            technology: item.technology || "",
                            amount_base: Number(item.amount_base) || 0,
                            course_id: toCourseId(item.course_id ?? item.id_treatment_plan_course),
                            appointments: Array.isArray(item.appointments)
                                ? item.appointments.map((n) => Number(n)).filter(Boolean)
                                : [],
                            subitems: Array.isArray(item.subitems)
                                ? item.subitems.map((sub, si) => ({
                                    id: sub.id || `s_${gi + 1}_${ii + 1}_${si + 1}`,
                                    name: sub.name || "",
                                    composition: sub.composition || "",
                                    technology: sub.technology || "",
                                    amount_base: Number(sub.amount_base) || 0,
                                }))
                                : [],
                        }))
                        : [],
                }))
                : [],
            courses: Array.isArray(raw.courses)
                ? raw.courses.map((c, ci) => ({
                    id: toCourseId(c.id) || `c_${ci + 1}`,
                    name: normalizeCourseTitle(c.name || ""),
                    color: c.color || COURSE_COLOR_OPTIONS[0]?.value || "#fffae0",
                    item_ids: Array.isArray(c.item_ids) ? c.item_ids.map((x) => String(x)) : [],
                }))
                : [],
        };
    }

    const count =
        Number(raw.count_appointment) ||
        Number(raw.count_appointment?.value) ||
        1;

    const groups =
        Array.isArray(raw.groups) && raw.groups.length > 0
            ? raw.groups
            : [];

    return {
        version: 1,
        count_appointment: count,
        groups: groups.map((g, gi) => ({
            id: g.id?.value || g.id || `g_${gi + 1}`,
            name: g.name?.value || g.name || "",
            type: g.type?.value || g.type || "procedure",
            count_appointment:
                Number(g.count_appointment?.value ?? g.count_appointment) || 0,
            column_type: getGroupColumnType({
                column_type: g.column_type?.value ?? g.column_type,
            }),
            items: Array.isArray(g.items)
                ? g.items.map((item, ii) => ({
                    id: item.id?.value || item.id || `i_${gi + 1}_${ii + 1}`,
                    name: item.name?.value || item.name || "",
                    composition: item.composition?.value || item.composition || "",
                    technology:
                        item.technology?.value !== undefined
                            ? item.technology.value
                            : item.technology || "",
                    amount_base:
                        Number(item.amount_base?.value || item.amount_base) || 0,
                    course_id: toCourseId(
                        item.course_id?.value ??
                            item.course_id ??
                            item.id_treatment_plan_course?.value ??
                            item.id_treatment_plan_course,
                    ),
                    appointments: Array.isArray(item.appointments)
                        ? item.appointments
                            .map((apt) =>
                                Number(
                                    apt.appointment_number?.value ||
                                    apt.appointment_number,
                                ),
                            )
                            .filter(Boolean)
                        : [],
                    subitems: Array.isArray(item.subitems)
                        ? item.subitems.map((sub, si) => ({
                            id: sub?.id?.value || sub?.id || `s_${gi + 1}_${ii + 1}_${si + 1}`,
                            name: sub?.name?.value || sub?.name || "",
                            composition: sub?.composition?.value || sub?.composition || "",
                            technology:
                                sub?.technology?.value !== undefined
                                    ? sub.technology.value
                                    : sub?.technology || "",
                            amount_base: Number(sub?.amount_base?.value || sub?.amount_base) || 0,
                        }))
                        : [],
                }))
                : [],
        })),
        courses: Array.isArray(raw.courses)
            ? raw.courses.map((c, ci) => ({
                id: toCourseId(c?.id?.value ?? c?.id) || `c_${ci + 1}`,
                name: normalizeCourseTitle(c?.name?.value ?? c?.name ?? ""),
                color:
                    c?.color?.value ??
                    c?.color ??
                    COURSE_COLOR_OPTIONS[0]?.value ??
                    "#fffae0",
                item_ids: Array.isArray(c?.item_ids?.value ?? c?.item_ids)
                    ? (c?.item_ids?.value ?? c?.item_ids).map((x) => String(x))
                    : [],
            }))
            : [],
    };
};

function TreatmentPlanTemplateView() {
    const { t } = useTranslation("templates");
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === "new";

    const weekDaysShort = useMemo(() => {
        const raw = String(t("weekdaysShort") || "");
        const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
        return list.length ? list : WEEK_DAYS_FALLBACK;
    }, [t]);

    const editServiceFieldsMemo = useMemo(() => editServiceFields(t), [t]);

    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isEditing, setIsEditing] = useState(isNew);
    const [templateMeta, setTemplateMeta] = useState({
        name: "",
        description: "",
    });
    const [templateType, setTemplateType] = useState("common"); // 'common' | 'my'
    const [templateStructure, setTemplateStructure] = useState(
        createEmptyStructure(),
    );
    const [saving, setSaving] = useState(false);
    const [serviceModal, setServiceModal] = useState({
        isOpen: false,
        groupId: null,
        services: [],
        selectedServiceId: "",
        mode: "add_service", // add_service | add_subitem
        parentItemId: null,
    });
    const [showEditServiceModal, setShowEditServiceModal] = useState(false);
    const [editingServiceContext, setEditingServiceContext] = useState(null);

    const [showAddCourseModal, setShowAddCourseModal] = useState(false);
    const [courseSelected, setCourseSelected] = useState([]);
    const [courseSelectedName, setCourseSelectedName] = useState("");
    const [courseSelectedColor, setCourseSelectedColor] = useState(
        COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0",
    );

    const [serviceSearchQuery, setServiceSearchQuery] = useState("");

    useEffect(() => {
        const defaultTitle = isNew
            ? t("planView.newTitle")
            : t("planView.title");

        document.title = templateMeta.name || defaultTitle;
    }, [isNew, templateMeta.name, t]);

    const renderGroupTypeLabel = (type) =>
        GROUP_TYPE_LABEL_KEYS[type] ? t(GROUP_TYPE_LABEL_KEYS[type]) : t("groupTypes.unknown");

    const loadTemplate = async () => {
        if (isNew) {
            setTemplate(null);
            setTemplateMeta({ name: "", description: "" });
            setTemplateStructure(createEmptyStructure());
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await GetData({
                module: "templates",
                block: "treatment_plan",
                action: "list",
            });

            const list = data.result || [];
            const found = list.find(
                (t) => String(t?.id?.value ?? t?.id) === String(id),
            );

            if (!found) {
                setError(t("planView.notFound"));
                setTemplate(null);
                return;
            }

            setTemplate(found);
            const name = found?.name?.value ?? found?.name ?? "";
            const description = found?.description?.value ?? found?.description ?? "";
            const rawJson =
                found?.structure_json?.value ?? found?.structure_json ?? "{}";

            let parsed = null;
            try {
                parsed = JSON.parse(rawJson || "{}");
            } catch (e) {
                parsed = null;
            }

            setTemplateMeta({ name, description });
            const createdBy =
                found?.id_user?.value ?? found?.id_user ?? 0;
            setTemplateType(createdBy === 0 ? "common" : "my");
            setTemplateStructure(normalizeStructure(parsed));
        } catch (err) {
            console.error("Ошибка загрузки шаблона:", err);
            setError(err.message || t("planView.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplate();
    }, [id]);

    const handleBack = () => {
        navigate("/templates/treatment_plans");
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        if (isNew) {
            navigate("/templates/treatment_plans");
            return;
        }
        setIsEditing(false);
        if (template) {
            const name = template?.name?.value ?? template?.name ?? "";
            const description = template?.description?.value ?? template?.description ?? "";
            const rawJson =
                template?.structure_json?.value ?? template?.structure_json ?? "{}";
            let parsed = null;
            try {
                parsed = JSON.parse(rawJson || "{}");
            } catch (e) {
                parsed = null;
            }
            setTemplateMeta({ name, description });
            setTemplateStructure(normalizeStructure(parsed));
        }
    };

    const handleDeleteClick = async () => {
        if (isNew || !id) return;

        const confirmDelete = window.confirm(t("planView.confirmDelete"));
        if (!confirmDelete) return;

        try {
            setLoading(true);
            await GetData({
                module: "templates",
                block: "treatment_plan",
                action: "delete",
                id_template: id,
            });

            // Проверяем, что шаблон действительно удалён
            try {
                const listData = await GetData({
                    module: "templates",
                    block: "treatment_plan",
                    action: "list",
                });

                const list = listData.result || [];
                const exists = list.some(
                    (t) => String(t?.id?.value ?? t?.id) === String(id),
                );

                if (exists) {
                    alert(t("planView.deleteFailed"));
                } else {
                    navigate("/templates/treatment_plans");
                }
            } catch (checkErr) {
                console.error("Ошибка проверки удаления шаблона:", checkErr);
                alert(t("planView.deleteCheckFailed"));
                navigate("/templates/treatment_plans");
            }
        } catch (err) {
            console.error("Ошибка удаления шаблона:", err);
            alert(t("planView.deleteFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleMetaChange = (field, value) => {
        setTemplateMeta((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleIncreaseGroupAppointments = (groupId) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) => {
                if (g.id !== groupId) return g;
                const current = getGroupAppointmentLimit(g, prev.count_appointment);
                return { ...g, count_appointment: current + 1 };
            }),
        }));
    };

    const handleDecreaseGroupAppointments = (groupId) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) => {
                if (g.id !== groupId) return g;
                const current = getGroupAppointmentLimit(g, prev.count_appointment);
                const next = Math.max(1, current - 1);
                return {
                    ...g,
                    count_appointment: next,
                    items: (g.items || []).map((item) => ({
                        ...item,
                        appointments: (item.appointments || []).filter((n) => n <= next),
                    })),
                };
            }),
        }));
    };

    const handleGroupColumnTypeChange = (groupId, nextTypeRaw) => {
        const nextType = Number(nextTypeRaw);
        if (!Number.isFinite(nextType) || nextType < 1 || nextType > 4) return;

        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId ? { ...g, column_type: nextType } : g,
            ),
        }));
    };

    const handleAddGroup = () => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: [
                ...prev.groups,
                {
                    id: `g_${Date.now()}`,
                    name: t("planView.newGroup"),
                    type: "procedure",
                    count_appointment: prev.count_appointment,
                    column_type: 1,
                    items: [],
                },
            ],
        }));
    };

    const handleGroupNameChange = (groupId, value) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId ? { ...g, name: value } : g,
            ),
        }));
    };

    const handleGroupTypeChange = (groupId, value) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId ? { ...g, type: value } : g,
            ),
        }));
    };

    const handleDeleteGroup = (groupId) => {
        const confirmDelete = window.confirm(t("planView.confirmDeleteGroup"));
        if (!confirmDelete) return;

        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.filter((g) => g.id !== groupId),
        }));
    };

    const handleAddService = async (groupId) => {
        const targetGroup = templateStructure.groups.find((g) => g.id === groupId);
        if (!targetGroup) {
            return;
        }

        const type = targetGroup.type || "procedure";

        try {
            const data = await GetData({
                module: "templates",
                block: "treatment_plan",
                action: "service_list",
                type,
            });

            const services = data.result || [];

            if (!Array.isArray(services) || services.length === 0) {
                alert(t("planView.noServicesForType"));
                return;
            }

            setServiceModal({
                isOpen: true,
                groupId,
                services,
                selectedServiceId: String(services[0]?.id ?? ""),
                mode: "add_service",
                parentItemId: null,
            });
            setServiceSearchQuery("");
        } catch (err) {
            console.error("Ошибка загрузки услуг для группы:", err);
            alert(t("planView.loadGroupServicesFailed"));
        }
    };

    const handleAddSubitem = async (groupId, parentItemId) => {
        const targetGroup = templateStructure.groups.find((g) => g.id === groupId);
        if (!targetGroup) return;

        const type = targetGroup.type || "procedure";

        try {
            const data = await GetData({
                module: "templates",
                block: "treatment_plan",
                action: "service_list",
                type,
            });

            const services = data.result || [];

            if (!Array.isArray(services) || services.length === 0) {
                alert(t("planView.noServicesForType"));
                return;
            }

            setServiceModal({
                isOpen: true,
                groupId,
                services,
                selectedServiceId: String(services[0]?.id ?? ""),
                mode: "add_subitem",
                parentItemId,
            });
            setServiceSearchQuery("");
        } catch (err) {
            console.error("Ошибка загрузки услуг для подуслуги:", err);
            alert(t("planView.loadSubitemServicesFailed"));
        }
    };

    const handleCloseServiceModal = () => {
        setServiceModal({
            isOpen: false,
            groupId: null,
            services: [],
            selectedServiceId: "",
            mode: "add_service",
            parentItemId: null,
        });
        setServiceSearchQuery("");
    };

    const handleSelectServiceFromSearch = (selectedService) => {
        if (!selectedService || !serviceModal.isOpen || !serviceModal.groupId) return;

        if (serviceModal.mode === "add_subitem" && serviceModal.parentItemId) {
            const sub = {
                id: selectedService.id,
                name: selectedService.name || "",
                composition: selectedService.composition || "",
                technology: selectedService.technology || "",
                amount_base: Number(selectedService.amount_base) || 0,
            };

            setTemplateStructure((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    g.id === serviceModal.groupId
                        ? {
                            ...g,
                            items: g.items.map((it) =>
                                it.id === serviceModal.parentItemId
                                    ? { ...it, subitems: [...(it.subitems || []), sub] }
                                    : it,
                            ),
                        }
                        : g,
                ),
            }));
        } else {
            setTemplateStructure((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    g.id === serviceModal.groupId
                        ? {
                            ...g,
                            items: [
                                ...g.items,
                                {
                                    id: selectedService.id,
                                    name: selectedService.name || "",
                                    composition: selectedService.composition || "",
                                    technology: selectedService.technology || "",
                                    amount_base: Number(selectedService.amount_base) || 0,
                                    course_id: null,
                                    appointments: [],
                                    subitems: [],
                                },
                            ],
                        }
                        : g,
                ),
            }));
        }

        handleCloseServiceModal();
    };

    const handleServiceFieldChange = (groupId, itemId, field, value) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        items: g.items.map((item) =>
                            item.id === itemId
                                ? {
                                    ...item,
                                    [field]:
                                        field === "amount_base"
                                            ? Number(value) || 0
                                            : value,
                                }
                                : item,
                        ),
                    }
                    : g,
            ),
        }));
    };

    const handleOpenEditService = (groupId, item) => {
        setEditingServiceContext({ groupId, itemId: item.id });
        setShowEditServiceModal(true);
    };

    const handleCloseEditServiceModal = () => {
        setShowEditServiceModal(false);
        setEditingServiceContext(null);
    };

    const handleSaveEditService = (updatedData) => {
        if (!editingServiceContext) return;
        const { groupId, itemId } = editingServiceContext;
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        items: g.items.map((item) =>
                            item.id === itemId
                                ? {
                                    ...item,
                                    name: updatedData.name ?? item.name,
                                    composition: updatedData.composition ?? item.composition,
                                    technology: updatedData.technology ?? item.technology,
                                    amount_base:
                                        updatedData.amount_base !== undefined && updatedData.amount_base !== null
                                            ? Number(updatedData.amount_base)
                                            : item.amount_base,
                                }
                                : item,
                        ),
                    }
                    : g,
            ),
        }));
        handleCloseEditServiceModal();
    };

    const editServiceInitialData = useMemo(() => {
        if (!editingServiceContext || !showEditServiceModal) return null;
        const group = templateStructure.groups.find((g) => g.id === editingServiceContext.groupId);
        const item = group?.items.find((i) => i.id === editingServiceContext.itemId);
        if (!item) return null;
        return {
            name: item.name ?? "",
            amount_base: item.amount_base ?? 0,
            composition: item.composition ?? "",
            technology: item.technology ?? "",
        };
    }, [editingServiceContext, showEditServiceModal, templateStructure.groups]);

    const handleDeleteService = (groupId, itemId) => {
        const confirmDelete = window.confirm(t("planView.confirmDeleteService"));
        if (!confirmDelete) return;

        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        items: g.items.filter((item) => item.id !== itemId),
                    }
                    : g,
            ),
            courses: (prev.courses || []).map((c) => ({
                ...c,
                item_ids: (c.item_ids || []).filter((x) => String(x) !== String(itemId)),
            })),
        }));
    };

    const handleDeleteSubitem = (groupId, itemId, subitemId) => {
        const confirmDelete = window.confirm(t("planView.confirmDeleteSubitem"));
        if (!confirmDelete) return;

        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        items: g.items.map((it) =>
                            it.id === itemId
                                ? {
                                    ...it,
                                    subitems: (it.subitems || []).filter(
                                        (s) => String(s.id) !== String(subitemId),
                                    ),
                                }
                                : it,
                        ),
                    }
                    : g,
            ),
        }));
    };

    const toggleAppointment = (groupId, itemId, number) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: prev.groups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        items: g.items.map((item) => {
                            if (item.id !== itemId) return item;
                            const exists = item.appointments.includes(number);
                            return {
                                ...item,
                                appointments: exists
                                    ? item.appointments.filter((n) => n !== number)
                                    : [...item.appointments, number],
                            };
                        }),
                    }
                    : g,
            ),
        }));
    };

    const renderAppointmentStatus = (item, appointmentNumber) => {
        if (!item.appointments) return "empty";
        return item.appointments.includes(appointmentNumber)
            ? "filled"
            : "empty";
    };

    const handleSaveTemplate = async () => {
        if (!templateMeta.name.trim()) {
            alert(t("planView.needName"));
            return;
        }

        setSaving(true);
        try {
            const baseRequest = {
                module: "templates",
                block: "treatment_plan",
            };
            // Общий / мой шаблон (0 / 4), на бэке поле хранится в created_by,
            // во входящих / исходящих данных используем id_user
            const idUser = templateType === "common" ? 0 : 4;

            const structureToSave = {
                version: 1,
                count_appointment: templateStructure.count_appointment,
                groups: templateStructure.groups,
                courses: templateStructure.courses || [],
            };

            let data;
            if (!isNew && id) {
                data = await GetData({
                    ...baseRequest,
                    action: "update",
                    id_template: id,
                    name: templateMeta.name.trim(),
                    description: templateMeta.description.trim(),
                    structure_json: JSON.stringify(structureToSave),
                    id_user: idUser,
                });
            } else {
                data = await GetData({
                    ...baseRequest,
                    action: "create",
                    name: templateMeta.name.trim(),
                    description: templateMeta.description.trim(),
                    structure_json: JSON.stringify(structureToSave),
                    id_user: idUser,
                });
            }

            if (data.status === "success") {
                if (data.result && (data.result.id?.value || data.result.id)) {
                    const newId = data.result.id?.value ?? data.result.id;
                    if (isNew) {
                        navigate(`/templates/treatment_plans/${newId}`, {
                            replace: true,
                        });
                    }
                    loadTemplate();
                }
                // После успешного сохранения выходим из режима редактирования
                setIsEditing(false);
            } else {
                alert(data.message || t("planView.saveFailed"));
            }
        } catch (err) {
            console.error("Ошибка сохранения шаблона:", err);
            alert(t("planView.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const allItemsFlat = useMemo(
        () =>
            (templateStructure.groups || []).flatMap((g) =>
                (g.items || []).map((it) => ({ ...it, __groupId: g.id })),
            ),
        [templateStructure.groups],
    );

    const courseIdToColor = useMemo(() => {
        const map = {};
        (templateStructure.courses || []).forEach((c) => {
            const id = toCourseId(c?.id);
            const color = c?.color;
            if (id && color) map[id] = color;
        });
        return map;
    }, [templateStructure.courses]);

    const courseIdToName = useMemo(() => {
        const map = {};
        (templateStructure.courses || []).forEach((c) => {
            const id = toCourseId(c?.id);
            const name = normalizeCourseTitle(c?.name);
            if (id && name) map[id] = name;
        });
        return map;
    }, [templateStructure.courses]);

    const serviceSearchOptions = useMemo(() => {
        const q = (serviceSearchQuery || "").trim().toLowerCase();
        const list = serviceModal.services || [];
        if (!q) return [];

        return list.filter((s) => {
            const name = String(s?.name || "").toLowerCase();
            const composition = String(s?.composition || "").toLowerCase();
            return name.includes(q) || composition.includes(q);
        });
    }, [serviceModal.services, serviceSearchQuery]);

    const handleToggleCourseItem = (itemId) => {
        setCourseSelected((prev) =>
            prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId],
        );
    };

    const handleCreateCourse = () => {
        const title = normalizeCourseTitle(courseSelectedName);
        if (!title || title.length < 2) {
            alert(t("planView.needCourseName"));
            return;
        }
        if (!courseSelected.length) {
            alert(t("planView.needCourseServices"));
            return;
        }

        const newId = createCourseId();
        const color =
            courseSelectedColor || (COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0");

        setTemplateStructure((prev) => {
            const itemIds = new Set(courseSelected.map((x) => String(x)));
            return {
                ...prev,
                courses: [
                    ...(prev.courses || []),
                    {
                        id: newId,
                        name: title,
                        color,
                        item_ids: courseSelected.map((x) => String(x)),
                    },
                ],
                groups: (prev.groups || []).map((g) => ({
                    ...g,
                    items: (g.items || []).map((it) => ({
                        ...it,
                        course_id: itemIds.has(String(it.id)) ? newId : toCourseId(it.course_id),
                    })),
                })),
            };
        });

        setShowAddCourseModal(false);
        setCourseSelected([]);
        setCourseSelectedName("");
        setCourseSelectedColor(COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0");
    };

    const handleRemoveItemFromCourse = (itemId) => {
        setTemplateStructure((prev) => ({
            ...prev,
            groups: (prev.groups || []).map((g) => ({
                ...g,
                items: (g.items || []).map((it) =>
                    String(it.id) === String(itemId) ? { ...it, course_id: null } : it,
                ),
            })),
            courses: (prev.courses || []).map((c) => ({
                ...c,
                item_ids: (c.item_ids || []).filter((x) => String(x) !== String(itemId)),
            })),
        }));
    };

    if (loading && !template && !isNew) {
        return (
            <Container fluid="md" className="mt-4">
                {t("state.loading", { ns: "common" })}
            </Container>
        );
    }

    if (error && !isNew) {
        return (
            <Container fluid="md" className="mt-4">
                <div className="text-danger mb-3">
                    {t("error", { ns: "common", message: error })}
                </div>
                <button className="button-default light" onClick={handleBack}>
                    {t("planView.backToList")}
                </button>
            </Container>
        );
    }

    return (
        <Container fluid="md" className="treatment-plan-template">
            <Row>
                <Col>
                    <div className="patient-page">
                        <TitleWithIcon
                            icon={templateIcon}
                            title={
                                templateMeta.name ||
                                (isNew ? t("planView.newTitle") : t("untitled", { ns: "common" }))
                            }
                            showStatus={false}
                            status={""}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="tab-content block page-block">
                        <Row>
                            <Col>
                                <BlockTitle
                                    title={templateMeta.name || "—"}
                                    buttons={
                                        isEditing
                                            ? [
                                                {
                                                    label: t("actions.save", { ns: "common" }),
                                                    onClick: handleSaveTemplate,
                                                    type: "normal",
                                                },
                                                {
                                                    label: t("actions.cancel", { ns: "common" }),
                                                    onClick: handleCancelEdit,
                                                    type: "light",
                                                },
                                                {
                                                    label: t("actions.delete", { ns: "common" }),
                                                    onClick: handleDeleteClick,
                                                    type: "danger",
                                                },
                                            ]
                                            : [
                                                ...(isNew
                                                    ? []
                                                    : [
                                                        {
                                                            label:(
                                                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                  <svg
                                                                    viewBox="0 0 24 24"
                                                                    aria-hidden="true"
                                                                    width="20"
                                                                    height="20"
                                                                    style={{ fill: "currentColor" }}>
                                                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                                                                  </svg>
                                                                  {t("actions.edit", { ns: "common" })}
                                                                </span>
                                                              ),
                                                            onClick: handleEditClick,
                                                            type: "light icon",
                                                        },
                                                        
                                                    ]),
                                                {
                                                    label: (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                          <svg
                                                            viewBox="0 0 24 24"
                                                            aria-hidden="true"
                                                            width="20"
                                                            height="20"
                                                            style={{ color: "currentColor" }}>
                                                            <path
                                                              d="M9 14L4 9l5-5"
                                                              fill="none"
                                                              stroke="currentColor"
                                                              strokeWidth="2.4"
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                            />
                                                            <path
                                                              d="M4 9h11a4 4 0 1 1 0 8H9"
                                                              fill="none"
                                                              stroke="currentColor"
                                                              strokeWidth="2.4"
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                            />
                                                          </svg>
                                                          {t("planView.backToListShort")}
                                                        </span>
                                                      ),
                                                    onClick: handleBack,
                                                    type: "normal icon",
                                                },
                                            ]
                                    }
                                />
                            </Col>
                        </Row>
                        <div className="service-container mt-4">
                            {!isEditing && !isNew ? (
                                <>
                                    <Row>
                                        <Col>
                                            <div className="page">
                                                <div className="page-content">
                                                    <div className="">
                                                        <div className="mt-2 text-muted small">
                                                            {t("planView.descriptionLabel")}{" "}
                                                            {templateMeta.description ? templateMeta.description : "—"}
                                                        </div>
                                                        <div className="mt-2 text-muted small">
                                                            {t("planView.typeLabel")}{" "}
                                                            {templateType === "common"
                                                                ? t("planView.typeCommon")
                                                                : t("planView.typeMy")}
                                                        </div>
                                                        {/* <div className="mt-2 text-muted small">
                                                            Приёмов в плане:{" "}
                                                            {templateStructure.count_appointment}
                                                            , групп: {templateStructure.groups.length}.
                                                        </div> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="mt-4">
                                        <Col>
                                            <div className="page">
                                                <div className="page-content">
                                                    <div className="">
                                                        <div className="patient-plan">
                                                            <div className="plan-groups">
                                                                {templateStructure.groups.map((group) => {
                                                                    const isWithoutAppointmentsGroup =
                                                                        isWithoutAppointmentsGroupType(group.type);
                                                                    const showAppointmentColumns =
                                                                        !isWithoutAppointmentsGroup;
                                                                    const groupAppointmentLimit = getGroupAppointmentLimit(
                                                                        group,
                                                                        templateStructure.count_appointment,
                                                                    );
                                                                    const appointmentHeaderType = getGroupColumnType(group);
                                                                    const groupAppointmentNumbers = showAppointmentColumns
                                                                        ? Array.from(
                                                                              { length: groupAppointmentLimit },
                                                                              (_, i) => i + 1,
                                                                          )
                                                                        : [];
                                                                    const groupTableColSpan =
                                                                        1 +
                                                                        (showAppointmentColumns
                                                                            ? groupAppointmentNumbers.length
                                                                            : 1);

                                                                    return (
                                                                    <div
                                                                        key={group.id}
                                                                        className="page-block group-section mt-3">
                                                                        <div className="table-wrapper">
                                                                            <div className="group-table">
                                                                                <table>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th className="service-name">
                                                                                                {renderGroupTypeLabel(group.type)}
                                                                                            </th>
                                                                                            {showAppointmentColumns ? (
                                                                                                groupAppointmentNumbers.map(
                                                                                                    (number) => (
                                                                                                        <th
                                                                                                            key={number}
                                                                                                            className="appointment-header">
                                                                                                            {getAppointmentHeaderLabel(
                                                                                                                number,
                                                                                                                appointmentHeaderType,
                                                                                                                t,
                                                                                                                weekDaysShort,
                                                                                                            )}
                                                                                                        </th>
                                                                                                    ),
                                                                                                )
                                                                                            ) : (
                                                                                                <th className="service-composition-header">
                                                                                                    {t("planView.composition")}
                                                                                                </th>
                                                                                            )}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {group.items.length === 0 ? (
                                                                                            <tr>
                                                                                                <td
                                                                                                    className="service-name  text-muted small"
                                                                                                    colSpan={groupTableColSpan}>
                                                                                                    {t("planView.noServicesInGroup")}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ) : (
                                                                                            (() => {
                                                                                                const sortedItems = [...(group.items || [])].sort((a, b) => {
                                                                                                    const aCourse = toCourseId(a?.course_id);
                                                                                                    const bCourse = toCourseId(b?.course_id);
                                                                                                    if (aCourse && !bCourse) return -1;
                                                                                                    if (!aCourse && bCourse) return 1;
                                                                                                    if (aCourse && bCourse && aCourse !== bCourse) {
                                                                                                        return String(aCourse).localeCompare(String(bCourse), "ru");
                                                                                                    }
                                                                                                    return String(a?.name || "").localeCompare(String(b?.name || ""), "ru");
                                                                                                });

                                                                                                const colSpan = groupTableColSpan;
                                                                                                const courseIdsWithItems = new Set(
                                                                                                    sortedItems
                                                                                                        .map((it) => toCourseId(it?.course_id))
                                                                                                        .filter(Boolean),
                                                                                                );

                                                                                                let lastCourseId = null;

                                                                                                return sortedItems.map((item) => {
                                                                                                    const courseId = toCourseId(item?.course_id);
                                                                                                    const isCourse = Boolean(courseId);
                                                                                                    const rowStyle =
                                                                                                        isCourse && courseIdToColor[courseId]
                                                                                                            ? { backgroundColor: courseIdToColor[courseId] }
                                                                                                            : undefined;

                                                                                                    const courseName = isCourse
                                                                                                        ? normalizeCourseTitle(courseIdToName[courseId] ?? "")
                                                                                                        : "";

                                                                                                    const shouldShowCourseTitle =
                                                                                                        !isWithoutAppointmentsGroup &&
                                                                                                        isCourse &&
                                                                                                        courseName &&
                                                                                                        courseIdsWithItems.has(courseId) &&
                                                                                                        courseId !== lastCourseId;
                                                                                                    if (shouldShowCourseTitle) lastCourseId = courseId;

                                                                                                    return (
                                                                                                        <React.Fragment key={item.id}>
                                                                                                            {shouldShowCourseTitle && (
                                                                                                                <tr className="course-title-row" style={rowStyle}>
                                                                                                                    <td className="service-name" colSpan={colSpan}>
                                                                                                                        <strong>{courseName}</strong>
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            )}
                                                                                                            <tr className="main-service" style={rowStyle}>
                                                                                                                <td className="service-name">
                                                                                                                    <div className="service-title">
                                                                                                                        <div className="mb-1">
                                                                                                                            {item.name || "—"}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                    {!isWithoutAppointmentsGroup && (
                                                                                                                        <div className="service-composition">
                                                                                                                            <div className="mb-1">
                                                                                                                                {item.composition || "—"}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                    {(item?.subitems || []).length > 0 && (
                                                                                                                        <div className="service-subitems">
                                                                                                                            {(item.subitems || []).map((sub) => (
                                                                                                                                <div key={sub?.id} className="service-subitem">
                                                                                                                                    <div className="mb-1">{sub?.name || "—"}</div>
                                                                                                                                    <div className="mb-1">{sub?.composition || "—"}</div>
                                                                                                                                    <div className="mb-1 text-muted">
                                                                                                                                        {Number(sub?.amount_base || 0)} ₽
                                                                                                                                    </div>
                                                                                                                                </div>
                                                                                                                            ))}
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                    <div className="service-price">
                                                                                                                        <div className="mb-1">
                                                                                                                            {Number(item.amount_base || 0).toLocaleString("ru-RU")} ₽
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </td>
                                                                                                                {showAppointmentColumns ? (
                                                                                                                    groupAppointmentNumbers.map((number) => (
                                                                                                                        <td key={number} className="appointment-cell">
                                                                                                                            <span
                                                                                                                                className={`appointment-indicator ${renderAppointmentStatus(
                                                                                                                                    item,
                                                                                                                                    number,
                                                                                                                                )}`}
                                                                                                                            />
                                                                                                                        </td>
                                                                                                                    ))
                                                                                                                ) : (
                                                                                                                    <td className="service-composition-cell">
                                                                                                                        <div className="mb-1">
                                                                                                                            {item.composition || "—"}
                                                                                                                        </div>
                                                                                                                    </td>
                                                                                                                )}
                                                                                                            </tr>
                                                                                                        </React.Fragment>
                                                                                                    );
                                                                                                });
                                                                                            })()
                                                                                        )}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <Row>
                                    <Col>
                                        <div className="page">
                                            <div className="page-content">
                                                <div className="">
                                                    <div className="patient-plan">
                                                        <div className="plan-header">
                                                            <div className="plan-meta">
                                                                <div className="plan-meta-field">
                                                                    <label className="mb-2">
                                                                        {t("planView.templateNameLabel")}
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control mb-2"
                                                                        value={templateMeta.name}
                                                                        onChange={(e) =>
                                                                            handleMetaChange(
                                                                                "name",
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                        placeholder={t("planView.templateNamePlaceholder")}
                                                                    />
                                                                </div>
                                                                <div className="plan-meta-field">
                                                                    <label>{t("planView.descriptionLabel")}</label>
                                                                    <textarea
                                                                        className="form-control mb-2"
                                                                        value={
                                                                            templateMeta.description
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleMetaChange(
                                                                                "description",
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                        rows={2}
                                                                        placeholder={t("planView.descriptionPlaceholder")}
                                                                    />
                                                                </div>
                                                                <div className="plan-meta-field">
                                                                    <label className="mb-2">
                                                                        {t("planView.typeLabel")}
                                                                    </label>
                                                                    <select
                                                                        className="form-control mb-2"
                                                                        value={templateType}
                                                                        onChange={(e) =>
                                                                            setTemplateType(e.target.value)
                                                                        }>
                                                                        <option value="common">{t("planView.typeCommon")}</option>
                                                                        <option value="my">{t("planView.typeMy")}</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="plan-meta-count_appointment">
                                                                <span>
                                                                    {t("planView.appointmentsCountLabel")}{" "}
                                                                    <strong>{t("planView.individuallyByGroups")}</strong>
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="plan-groups">
                                                            {templateStructure.groups.map(
                                                                (group) => {
                                                                    const isWithoutAppointmentsGroup =
                                                                        isWithoutAppointmentsGroupType(group.type);
                                                                    const showAppointmentColumns =
                                                                        !isWithoutAppointmentsGroup;
                                                                    const groupAppointmentLimit = getGroupAppointmentLimit(
                                                                        group,
                                                                        templateStructure.count_appointment,
                                                                    );
                                                                    const appointmentHeaderType = getGroupColumnType(group);
                                                                    const groupAppointmentNumbers = showAppointmentColumns
                                                                        ? Array.from(
                                                                              { length: groupAppointmentLimit },
                                                                              (_, i) => i + 1,
                                                                          )
                                                                        : [];

                                                                    return (
                                                                    <React.Fragment key={group.id}>
                                                                    {showAppointmentColumns && (
                                                                        <div className="plan-count_appointment-row">
                                                                            <div className="plan-count_appointment">
                                                                                <div className="plan-name-count_appointment">
                                                                                    {t("planView.appointmentCountCol")}
                                                                                </div>
                                                                                <div className="plan-count_appointment-value">
                                                                                    <div
                                                                                        className="plan-minus-count_appointment"
                                                                                        onClick={() =>
                                                                                            handleDecreaseGroupAppointments(
                                                                                                group.id,
                                                                                            )
                                                                                        }>
                                                                                        -
                                                                                    </div>
                                                                                    <div className="plan-result-count_appointment">
                                                                                        {groupAppointmentLimit || ""}
                                                                                    </div>
                                                                                    <div
                                                                                        className="plan-plus-count_appointment"
                                                                                        onClick={() =>
                                                                                            handleIncreaseGroupAppointments(
                                                                                                group.id,
                                                                                            )
                                                                                        }>
                                                                                        +
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <select
                                                                                className="plan-column-type-select"
                                                                                value={appointmentHeaderType}
                                                                                onChange={(event) =>
                                                                                    handleGroupColumnTypeChange(
                                                                                        group.id,
                                                                                        event.target.value,
                                                                                    )
                                                                                }>
                                                                                <option value={1}>{t("planView.colTypeAppointments")}</option>
                                                                                <option value={2}>{t("planView.colTypeConsecutiveDays")}</option>
                                                                                <option value={3}>{t("planView.colTypeEvenDays")}</option>
                                                                                <option value={4}>{t("planView.colTypeOddDays")}</option>
                                                                            </select>
                                                                        </div>
                                                                    )}
                                                                    <div
                                                                        className="page-block group-section">
                                                                        <div className="group-header-edit">
                                                                            <select
                                                                                className="form-control mb-2"
                                                                                value={group.type || "procedure"}
                                                                                onChange={(e) =>
                                                                                    handleGroupTypeChange(
                                                                                        group.id,
                                                                                        e.target.value,
                                                                                    )
                                                                                }>
                                                                                {GROUP_TYPE_OPTIONS.map(({ value, labelKey }) => (
                                                                                    <option key={value} value={value}>
                                                                                        {t(labelKey)}
                                                                                    </option>
                                                                                ))}
                                                                            </select>

                                                                        </div>

                                                                        <div className="table-wrapper">
                                                                            <div className="group-table">
                                                                                <table>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th className="service-name">
                                                                                                {renderGroupTypeLabel(group.type)}
                                                                                            </th>
                                                                                            {showAppointmentColumns ? (
                                                                                                groupAppointmentNumbers.map(
                                                                                                    (
                                                                                                        number,
                                                                                                    ) => (
                                                                                                        <th
                                                                                                            key={
                                                                                                                number
                                                                                                            }
                                                                                                            className="appointment-header">
                                                                                                            {getAppointmentHeaderLabel(
                                                                                                                number,
                                                                                                                appointmentHeaderType,
                                                                                                                t,
                                                                                                                weekDaysShort,
                                                                                                            )}
                                                                                                        </th>
                                                                                                    ),
                                                                                                )
                                                                                            ) : (
                                                                                                <th className="service-composition-header">
                                                                                                    {t("planView.composition")}
                                                                                                </th>
                                                                                            )}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {group.items.map(
                                                                                            (
                                                                                                item,
                                                                                            ) => (
                                                                                                    <tr
                                                                                                        key={
                                                                                                            item.id
                                                                                                        }
                                                                                                        className="main-service">
                                                                                                        <td className="service-name">
                                                                                                            <div className="service-title">
                                                                                                                <div className="mb-2">
                                                                                                                    {item.name ||
                                                                                                                        "—"}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                            {!isWithoutAppointmentsGroup && (
                                                                                                                <div className="service-composition">
                                                                                                                    <div className="mb-2">
                                                                                                                        {item
                                                                                                                            .composition ||
                                                                                                                            "—"}
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            )}
                                                                                                            {(item?.subitems || []).length > 0 && (
                                                                                                                <div className="service-subitems">
                                                                                                                    {(item.subitems || []).map((sub) => (
                                                                                                                        <div
                                                                                                                            key={sub?.id}
                                                                                                                            className="service-subitem d-flex gap-2 align-items-start">
                                                                                                                            <div style={{ flex: 1 }}>
                                                                                                                                <div className="mb-1">
                                                                                                                                    {sub?.name || "—"}
                                                                                                                                </div>
                                                                                                                                <div className="mb-1">
                                                                                                                                    {sub?.composition || "—"}
                                                                                                                                </div>
                                                                                                                                <div className="mb-1 text-muted">
                                                                                                                                    {Number(sub?.amount_base || 0)} ₽
                                                                                                                                </div>
                                                                                                                            </div>
                                                                                                                            <div
                                                                                                                                className="changed-button changed-button-delete"
                                                                                                                                onClick={() =>
                                                                                                                                    handleDeleteSubitem(
                                                                                                                                        group.id,
                                                                                                                                        item.id,
                                                                                                                                        sub?.id,
                                                                                                                                    )
                                                                                                                                }>
                                                                                                                                {t("planView.removeFromComposition")}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    ))}
                                                                                                                </div>
                                                                                                            )}
                                                                                                            <div className="service-price">
                                                                                                                <div className="mb-2">
                                                                                                                    {item.amount_base ||
                                                                                                                        0}{" "}
                                                                                                                    ₽
                                                                                                                </div>
                                                                                                            </div>
                                                                                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                                                                                                <div
                                                                                                                    className="changed-button"
                                                                                                                    onClick={() =>
                                                                                                                        handleOpenEditService(
                                                                                                                            group.id,
                                                                                                                            item,
                                                                                                                        )
                                                                                                                    }>
                                                                                                                    {t("planView.change")}
                                                                                                                </div>
                                                                                                                <div
                                                                                                                    className="changed-button"
                                                                                                                    onClick={() =>
                                                                                                                        handleAddSubitem(
                                                                                                                            group.id,
                                                                                                                            item.id,
                                                                                                                        )
                                                                                                                    }>
                                                                                                                    {t("planView.addToComposition")}
                                                                                                                </div>
                                                                                                                <div
                                                                                                                    className="changed-button"
                                                                                                                    onClick={() =>
                                                                                                                        handleDeleteService(
                                                                                                                            group.id,
                                                                                                                            item.id,
                                                                                                                        )
                                                                                                                    }>
                                                                                                                    {t("planView.delete")}
                                                                                                                </div>
                                                                                                                {toCourseId(item?.course_id) ? (
                                                                                                                    <div
                                                                                                                        className="changed-button changed-button-delete"
                                                                                                                        onClick={() =>
                                                                                                                            handleRemoveItemFromCourse(
                                                                                                                                item.id,
                                                                                                                            )
                                                                                                                        }>
                                                                                                                        {t("planView.removeFromCourse")}
                                                                                                                    </div>
                                                                                                                ) : null}
                                                                                                            </div>
                                                                                                        </td>
                                                                                                    {showAppointmentColumns ? (
                                                                                                        groupAppointmentNumbers.map(
                                                                                                            (
                                                                                                                number,
                                                                                                            ) => (
                                                                                                                <td
                                                                                                                    key={
                                                                                                                        number
                                                                                                                    }
                                                                                                                    className="appointment-cell">
                                                                                                                    <span
                                                                                                                        onClick={() =>
                                                                                                                            toggleAppointment(
                                                                                                                                group.id,
                                                                                                                                item.id,
                                                                                                                                number,
                                                                                                                            )
                                                                                                                        }
                                                                                                                        className={`appointment-indicator ${renderAppointmentStatus(
                                                                                                                            item,
                                                                                                                            number,
                                                                                                                        )} changed`}
                                                                                                                    />
                                                                                                                </td>
                                                                                                            ),
                                                                                                        )
                                                                                                    ) : (
                                                                                                        <td className="service-composition-cell">
                                                                                                            <div className="mb-1">
                                                                                                                {item.composition || "—"}
                                                                                                            </div>
                                                                                                        </td>
                                                                                                    )}
                                                                                                </tr>
                                                                                            ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>

                                                                        <div className="btn-add-service">
                                                                            <button
                                                                                className="btn-base"
                                                                                onClick={() =>
                                                                                        handleAddService(
                                                                                        group.id,
                                                                                    )
                                                                                }>
                                                                                {t("planView.addService")}
                                                                            </button>
                                                                        </div>
                                                                        <button
                                                                                className="button-default danger-light m-2"
                                                                                onClick={() =>
                                                                                    handleDeleteGroup(
                                                                                        group.id,
                                                                                    )
                                                                                }>
                                                                                {t("planView.deleteGroup")}
                                                                        </button>
                                                                    </div>
                                                                    </React.Fragment>
                                                                    );
                                                                },
                                                            )}
                                                        </div>

                                                        <div className="btn-add-group">
                                                            <button
                                                                className="button-default light"
                                                                onClick={handleAddGroup}>
                                                                {t("planView.addGroup")}
                                                            </button>
                                                        </div>

                                                        <div className="mt-3">
                                                            <button
                                                                className="button-default light"
                                                                onClick={() => {
                                                                    setShowAddCourseModal(true);
                                                                    setCourseSelected([]);
                                                                    setCourseSelectedName("");
                                                                    setCourseSelectedColor(
                                                                        COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0",
                                                                    );
                                                                }}>
                                                                {t("planView.addCourse")}
                                                            </button>
                                                        </div>

                                                        <div className="mt-4">
                                                            <button
                                                                className="button-default success"
                                                                onClick={handleSaveTemplate}
                                                                disabled={
                                                                    saving ||
                                                                    !templateMeta.name.trim()
                                                                }>
                                                                {t("planView.saveTemplate")}
                                                            </button>
                                                        </div>

                                                        <SearchSelectModal
                                                            show={serviceModal.isOpen}
                                                            onHide={handleCloseServiceModal}
                                                            title={
                                                                serviceModal.mode === "add_subitem"
                                                                    ? t("planView.selectSubservice")
                                                                    : t("planView.selectService")
                                                            }
                                                            query={serviceSearchQuery}
                                                            onQueryChange={setServiceSearchQuery}
                                                            loading={false}
                                                            options={serviceSearchOptions}
                                                            onSelect={handleSelectServiceFromSearch}
                                                            optionKey={(s) => String(s.id)}
                                                            optionLabel={(s) => s.name}
                                                            optionSubLabel={(s) => s.composition}
                                                            minQueryLength={2}
                                                            hint={t("planView.searchHint")}
                                                            emptyMessage={t("planView.searchEmpty")}
                                                            loadingMessage={t("planView.searchLoading")}
                                                        />

                                                        <EditableModal
                                                            show={showEditServiceModal}
                                                            onHide={handleCloseEditServiceModal}
                                                            title={t("planView.editServiceTitle")}
                                                            initialData={editServiceInitialData || {}}
                                                            fields={editServiceFieldsMemo}
                                                            onSave={handleSaveEditService}
                                                        />

                                                        <Modal
                                                            show={showAddCourseModal}
                                                            onHide={() => setShowAddCourseModal(false)}
                                                            centered>
                                                            <Modal.Header closeButton>
                                                                <Modal.Title>{t("planView.addCourseTitle")}</Modal.Title>
                                                            </Modal.Header>
                                                            <Modal.Body>
                                                                <div className="mb-3">
                                                                    <label className="form-label">
                                                                        {t("planView.courseNameLabel")}
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={courseSelectedName}
                                                                        onChange={(e) =>
                                                                            setCourseSelectedName(e.target.value)
                                                                        }
                                                                        placeholder={t("planView.courseNamePlaceholder")}
                                                                    />
                                                                </div>
                                                                <div className="mb-3">
                                                                    <label className="form-label">
                                                                        {t("planView.courseColorLabel")}
                                                                    </label>
                                                                    <select
                                                                        className="form-select"
                                                                        value={courseSelectedColor}
                                                                        onChange={(e) =>
                                                                            setCourseSelectedColor(e.target.value)
                                                                        }>
                                                                        {COURSE_COLOR_OPTIONS.map((opt) => (
                                                                            <option
                                                                                key={opt.value}
                                                                                value={opt.value}>
                                                                                {t(opt.labelKey)}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <label className="form-label">
                                                                        {t("planView.courseServicesLabel")}
                                                                    </label>
                                                                    <div className="text-muted small mb-2">
                                                                        {t("planView.courseServicesHint")}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            maxHeight: 260,
                                                                            overflow: "auto",
                                                                            border: "1px solid #eee",
                                                                            borderRadius: 6,
                                                                            padding: 10,
                                                                        }}>
                                                                        {allItemsFlat.length === 0 ? (
                                                                            <div className="text-muted">
                                                                                {t("planView.noServicesInTemplate")}
                                                                            </div>
                                                                        ) : (
                                                                            allItemsFlat.map((it) => (
                                                                                <label
                                                                                    key={it.id}
                                                                                    className="d-flex align-items-center gap-2 mb-2">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={courseSelected.includes(it.id)}
                                                                                        disabled={Boolean(
                                                                                            toCourseId(it.course_id),
                                                                                        )}
                                                                                        onChange={() =>
                                                                                            handleToggleCourseItem(
                                                                                                it.id,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    <span>
                                                                                        {it.name || it.id}
                                                                                        {toCourseId(it.course_id) ? (
                                                                                            <span className="text-muted">
                                                                                                {" "}
                                                                                                {t("planView.alreadyInCourse")}
                                                                                            </span>
                                                                                        ) : null}
                                                                                    </span>
                                                                                </label>
                                                                            ))
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </Modal.Body>
                                                            <Modal.Footer>
                                                                <button
                                                                    className="button-default light"
                                                                    onClick={() => setShowAddCourseModal(false)}>
                                                                    {t("actions.cancel", { ns: "common" })}
                                                                </button>
                                                                <button
                                                                    className="button-default"
                                                                    onClick={handleCreateCourse}>
                                                                    {t("planView.createCourse")}
                                                                </button>
                                                            </Modal.Footer>
                                                        </Modal>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default TreatmentPlanTemplateView;

